import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPencil,
  faTrash,
  faSpinner,
  faScrewdriverWrench,
} from '@fortawesome/free-solid-svg-icons';
import {
  accessoryService,
  type Accessory,
  type CreateAccessoryPayload,
} from '@/services/accessory.service';
import { SkeletonPage } from '@/components/skeletons';
import { PageHeader } from '@/components/shared/PageHeader';
import { SearchBar } from '@/components/shared/SearchBar';
import { EmptyState } from '@/components/shared/EmptyState';
import { Pagination } from '@/components/shared/Pagination';
import { Modal } from '@/components/shared/Modal';
import { FormField } from '@/components/shared/FormField';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { toast, confirmDelete } from '@/lib/swal';
import { useDebounce } from '@/hooks/use-debounce';
import { useHasPermission } from '@/hooks/use-permissions';

const PAGE_SIZE = 20;

interface AccessoryFormProps {
  initial?: Partial<CreateAccessoryPayload> & { isActive?: boolean };
  onSubmit: (d: CreateAccessoryPayload) => void;
  onCancel: () => void;
  saving: boolean;
}

function AccessoryForm({ initial, onSubmit, onCancel, saving }: AccessoryFormProps) {
  const [name, setName] = useState(initial?.name ?? '');
  const [description, setDescription] = useState(initial?.description ?? '');
  const [errors, setErrors] = useState<Record<string, string>>({});

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const errs: Record<string, string> = {};
    if (!name.trim()) errs.name = 'Name is required';
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      return;
    }
    setErrors({});
    onSubmit({ name: name.trim(), description: description.trim() || undefined });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <FormField label="Name" htmlFor="acc-name" required error={errors.name}>
        <Input
          id="acc-name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g. Battery, Power Cable, ECG Cable"
          disabled={saving}
          maxLength={255}
        />
      </FormField>
      <FormField label="Description" htmlFor="acc-desc">
        <Textarea
          id="acc-desc"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Optional description"
          rows={3}
          disabled={saving}
        />
      </FormField>
      <div className="flex justify-end gap-3 pt-2">
        <Button type="button" variant="ghost" onClick={onCancel} disabled={saving}>
          Cancel
        </Button>
        <Button type="submit" disabled={saving} className="min-w-[100px]">
          {saving ? <FontAwesomeIcon icon={faSpinner} className="animate-spin" /> : 'Save'}
        </Button>
      </div>
    </form>
  );
}

export function AccessoriesPage() {
  const queryClient = useQueryClient();
  const can = useHasPermission();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 350);
  const [modalOpen, setModalOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<Accessory | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['accessories', page, debouncedSearch],
    queryFn: () =>
      accessoryService.list({ page, pageSize: PAGE_SIZE, search: debouncedSearch || undefined }),
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['accessories'] });

  const createMutation = useMutation({
    mutationFn: accessoryService.create,
    onSuccess: () => {
      toast.success('Accessory created');
      setModalOpen(false);
      invalidate();
    },
    onError: () => toast.error('Failed to create accessory'),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: Partial<CreateAccessoryPayload> }) =>
      accessoryService.update(id, payload),
    onSuccess: () => {
      toast.success('Accessory updated');
      setModalOpen(false);
      setEditTarget(null);
      invalidate();
    },
    onError: () => toast.error('Failed to update accessory'),
  });

  const deleteMutation = useMutation({
    mutationFn: accessoryService.remove,
    onSuccess: () => {
      toast.success('Accessory deleted');
      invalidate();
    },
    onError: () => toast.error('Failed to delete accessory'),
  });

  const handleDelete = async (a: Accessory) => {
    if (await confirmDelete(a.name)) deleteMutation.mutate(a.id);
  };

  const openCreate = () => {
    setEditTarget(null);
    setModalOpen(true);
  };
  const openEdit = (a: Accessory) => {
    setEditTarget(a);
    setModalOpen(true);
  };
  const closeModal = () => {
    setModalOpen(false);
    setEditTarget(null);
  };
  const handleSearch = (v: string) => {
    setSearch(v);
    setPage(1);
  };
  const saving = createMutation.isPending || updateMutation.isPending;

  if (isLoading) return <SkeletonPage />;

  return (
    <div className="space-y-5">
      <PageHeader
        title="Accessories"
        description="Reusable accessory master — assign defaults to equipment models"
        onAdd={can('accessory:create') ? openCreate : undefined}
        addLabel="Add Accessory"
      />
      <SearchBar value={search} onChange={handleSearch} placeholder="Search accessories..." />

      {data && data.total > 0 ? (
        <>
          <div className="overflow-x-auto rounded-lg border border-border">
            <table className="w-full min-w-[500px] text-sm">
              <thead>
                <tr className="border-b border-border bg-muted/40 text-left">
                  <th className="px-4 py-3 font-medium text-foreground">Name</th>
                  <th className="px-4 py-3 font-medium text-foreground">Description</th>
                  <th className="px-4 py-3 font-medium text-foreground">Status</th>
                  <th className="px-4 py-3 font-medium text-foreground sr-only">Actions</th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((a) => (
                  <tr key={a.id} className="border-b border-border last:border-0 hover:bg-muted/20">
                    <td className="px-4 py-3 font-medium text-foreground">{a.name}</td>
                    <td className="px-4 py-3 text-muted-foreground">{a.description ?? '—'}</td>
                    <td className="px-4 py-3">
                      <span
                        className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-medium ${
                          a.isActive
                            ? 'bg-primary/10 text-primary'
                            : 'bg-muted text-muted-foreground'
                        }`}
                      >
                        {a.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1">
                        {can('accessory:update') && (
                          <button
                            onClick={() => openEdit(a)}
                            className="flex h-8 w-8 items-center justify-center rounded-lg text-muted-foreground hover:bg-muted hover:text-foreground"
                            aria-label={`Edit ${a.name}`}
                          >
                            <FontAwesomeIcon icon={faPencil} className="h-3.5 w-3.5" />
                          </button>
                        )}
                        {can('accessory:delete') && (
                          <button
                            onClick={() => handleDelete(a)}
                            className="flex h-8 w-8 items-center justify-center rounded-lg text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
                            aria-label={`Delete ${a.name}`}
                            disabled={deleteMutation.isPending}
                          >
                            <FontAwesomeIcon icon={faTrash} className="h-3.5 w-3.5" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Pagination
            page={data.page}
            pageSize={data.pageSize}
            total={data.total}
            onPageChange={setPage}
          />
        </>
      ) : (
        <EmptyState
          icon={faScrewdriverWrench}
          title={search ? 'No accessories match your search' : 'No accessories yet'}
          description={
            search
              ? 'Try a different search term.'
              : 'Add accessories like Battery, Adapter, ECG Cable to assign as model defaults.'
          }
          action={!search && can('accessory:create') ? <Button onClick={openCreate}>Add Accessory</Button> : undefined}
        />
      )}

      <Modal
        open={modalOpen}
        onClose={closeModal}
        title={editTarget ? 'Edit Accessory' : 'Add Accessory'}
        size="md"
      >
        <AccessoryForm
          initial={
            editTarget
              ? { name: editTarget.name, description: editTarget.description ?? undefined }
              : undefined
          }
          onSubmit={(p) =>
            editTarget
              ? updateMutation.mutate({ id: editTarget.id, payload: p })
              : createMutation.mutate(p)
          }
          onCancel={closeModal}
          saving={saving}
        />
      </Modal>
    </div>
  );
}
